/**
 * GlassButton - Modern button with glassmorphism effect
 * Hover animations and click feedback
 */

import { UIColors, UIAlpha, UIBorderRadius, UITypography, UIAnimation } from '../UIDesignTokens';

export default class GlassButton {
    constructor(scene, x, y, text, config = {}) {
        this.scene = scene;
        this.x = x;
        this.y = y;
        this.text = text;
        
        this.config = {
            width: config.width ?? 260,
            height: config.height ?? 54,
            bgColor: config.bgColor ?? UIColors.primary,
            bgAlpha: config.bgAlpha ?? 0.85,
            hoverColor: config.hoverColor ?? UIColors.primaryLight,
            textColor: config.textColor ?? '#ffffff',
            fontSize: config.fontSize ?? '18px',
            fontWeight: config.fontWeight ?? UITypography.weightSemibold,
            cornerRadius: config.cornerRadius ?? UIBorderRadius.md,
            icon: config.icon ?? null,
            disabled: config.disabled ?? false,
            onClick: config.onClick ?? (() => {}),
            ...config
        };
        
        this.container = scene.add.container(x, y);
        this.isHovered = false;
        this.isPressed = false;
        
        this.create();
    }

    create() {
        const { width, height, cornerRadius } = this.config;
        
        // Shadow
        this.shadow = this.scene.add.graphics();
        this.shadow.fillStyle(UIColors.black, 0.25);
        this.drawRoundedRect(this.shadow, 2, 3, width, height, cornerRadius);
        this.container.add(this.shadow);
        
        // Background
        this.background = this.scene.add.graphics();
        this.updateBackground(this.config.bgColor, this.config.bgAlpha);
        this.container.add(this.background);
        
        // Highlight (top edge)
        this.highlight = this.scene.add.graphics();
        this.highlight.fillStyle(UIColors.white, 0.15);
        this.highlight.fillRect(-width/2 + 4, -height/2 + 2, width - 8, 2);
        this.container.add(this.highlight);
        
        // Border
        this.border = this.scene.add.graphics();
        this.border.lineStyle(1, UIColors.white, 0.2);
        this.drawRoundedRect(this.border, 0, 0, width, height, cornerRadius, false);
        this.container.add(this.border);
        
        // Text
        this.textElement = this.scene.add.text(0, 0, this.text, {
            fontSize: this.config.fontSize,
            fontFamily: UITypography.fontFamily,
            fontStyle: this.config.fontWeight,
            fill: this.config.textColor,
            align: 'center'
        }).setOrigin(0.5);
        this.container.add(this.textElement);
        
        // Icon if provided
        if (this.config.icon) {
            this.iconElement = this.scene.add.text(-this.config.width/2 + 20, 0, this.config.icon, {
                fontSize: '20px',
                fill: this.config.textColor
            }).setOrigin(0, 0.5);
            this.container.add(this.iconElement);
        }
        
        // Interaction
        this.setupInteraction();
        
        // Initial state
        if (this.config.disabled) {
            this.setDisabled(true);
        }
    }

    drawRoundedRect(graphics, offsetX, offsetY, width, height, radius, fill = true) {
        const x = -width/2 + offsetX;
        const y = -height/2 + offsetY;
        
        graphics.beginPath();
        graphics.moveTo(x + radius, y);
        graphics.lineTo(x + width - radius, y);
        graphics.arc(x + width - radius, y + radius, radius, -Math.PI / 2, 0);
        graphics.lineTo(x + width, y + height - radius);
        graphics.arc(x + width - radius, y + height - radius, radius, 0, Math.PI / 2);
        graphics.lineTo(x + radius, y + height);
        graphics.arc(x + radius, y + height - radius, radius, Math.PI / 2, Math.PI);
        graphics.lineTo(x, y + radius);
        graphics.arc(x + radius, y + radius, radius, Math.PI, Math.PI * 1.5);
        graphics.closePath();
        
        if (fill) {
            graphics.fillPath();
        } else {
            graphics.strokePath();
        }
    }

    updateBackground(color, alpha) {
        this.background.clear();
        this.background.fillStyle(color, alpha);
        this.drawRoundedRect(this.background, 0, 0, this.config.width, this.config.height, this.config.cornerRadius);
    }

    setupInteraction() {
        // Create invisible hit area
        const hitArea = this.scene.add.rectangle(0, 0, this.config.width, this.config.height, 0x000000, 0);
        hitArea.setInteractive({ useHandCursor: true });
        this.container.add(hitArea);
        
        hitArea.on('pointerover', () => this.onHoverStart());
        hitArea.on('pointerout', () => this.onHoverEnd());
        hitArea.on('pointerdown', () => this.onPress());
        hitArea.on('pointerup', () => this.onRelease());
        
        this.hitArea = hitArea;
    }

    onHoverStart() {
        if (this.config.disabled) return;
        this.isHovered = true;
        
        this.scene.tweens.add({
            targets: this.container,
            scaleX: 1.03,
            scaleY: 1.03,
            duration: UIAnimation.fast,
            ease: UIAnimation.easeOut
        });
        
        this.updateBackground(this.config.hoverColor, this.config.bgAlpha + 0.1);
    }

    onHoverEnd() {
        if (this.config.disabled) return;
        this.isHovered = false;
        
        this.scene.tweens.add({
            targets: this.container,
            scaleX: 1,
            scaleY: 1,
            duration: UIAnimation.fast,
            ease: UIAnimation.easeOut
        });
        
        this.updateBackground(this.config.bgColor, this.config.bgAlpha);
    }

    onPress() {
        if (this.config.disabled) return;
        this.isPressed = true;
        
        this.scene.tweens.add({
            targets: this.container,
            scaleX: 0.97,
            scaleY: 0.97,
            duration: 50,
            ease: UIAnimation.easeOut
        });
    }

    onRelease() {
        if (this.config.disabled) return;
        this.isPressed = false;
        
        this.scene.tweens.add({
            targets: this.container,
            scaleX: this.isHovered ? 1.03 : 1,
            scaleY: this.isHovered ? 1.03 : 1,
            duration: UIAnimation.fast,
            ease: UIAnimation.bounce
        });
        
        // Trigger callback
        this.config.onClick();
    }

    setDisabled(disabled) {
        this.config.disabled = disabled;
        this.container.setAlpha(disabled ? 0.5 : 1);
        
        if (this.hitArea) {
            if (disabled) {
                this.hitArea.disableInteractive();
            } else {
                this.hitArea.setInteractive({ useHandCursor: true });
            }
        }
    }

    setText(text) {
        this.text = text;
        if (this.textElement) {
            this.textElement.setText(text);
        }
    }

    animateIn(delay = 0) {
        this.container.setAlpha(0);
        this.container.setScale(0.8);
        
        this.scene.tweens.add({
            targets: this.container,
            alpha: 1,
            scaleX: 1,
            scaleY: 1,
            duration: UIAnimation.normal,
            delay: delay,
            ease: UIAnimation.bounce
        });
    }

    setPosition(x, y) {
        this.container.setPosition(x, y);
    }

    setDepth(depth) {
        this.container.setDepth(depth);
    }

    destroy() {
        this.container.destroy();
    }
}
